import React from 'react';
import formatDate from 'date-fns/format';
import get from 'lodash/get';
import { Avatar, Box, Theme, Typography } from '@mui/material';
import { CustomKeyTypesEnum, ProfileAttributes } from '@loyalty/shared-common/profile-api-client';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { IdentityProperties, useIdentityProperties } from '../../../rules/rule-table-constants';
import { useCustomKeys } from '../../../custom-keys/CustomKeysContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles<Theme>(({ spacing }) => ({
  list: {
    margin: 0,
    padding: 0,
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing(1, 0),
  },
  property: {
    fontWeight: '600 !important',
    width: '40%',
  },
  value: {
    width: '60%',
    marginLeft: '5px',
  },
  picture: {
    height: 108,
    marginLeft: spacing(-3),
    width: 108,
  },
}));

interface Props {
  profile: ProfileAttributes;
}

const PROPERTIES = Object.values(IdentityProperties);

function renderCustomKeyValue(
  technicalName: string,
  type: CustomKeyTypesEnum,
  timeDateFormat: string,
  profileCustomKeys: Record<string, any> | undefined,
): string {
  const value = profileCustomKeys?.[technicalName];

  if (type === CustomKeyTypesEnum.BOOLEAN && value) {
    return value.toString();
  }

  if (type === CustomKeyTypesEnum.DATE && value) {
    try {
      return formatDate(new Date(value), timeDateFormat);
    } catch (e) {
      return value;
    }
  }

  return value || '-';
}

export function ProfileDetailsList({ profile }: Props): React.ReactElement {
  const classes = useStyles();
  const { programId } = useProgram();
  const { t } = useTranslation();
  const membership = profile.memberships?.find(membership => membership.programId === programId);
  const timeDateFormat = t('timeDateFormat') ?? 'dd/MM/yyyy HH:mm:ss';
  const { customKeys } = useCustomKeys();
  const sourceTranslationIdentityProperties = useIdentityProperties();

  return (
    <ul className={classes.list}>
      {PROPERTIES.map(prop => (
        <Box key={prop} className={classes.listItem} component="li">
          <Typography variant="body1" component="div" className={classes.property}>
            {sourceTranslationIdentityProperties[prop]}
          </Typography>
          <Typography variant="body1" component="div" className={classes.value}>
            {prop === 'picture' ? (
              <Avatar className={classes.picture} src={get(profile, prop)} />
            ) : (
              get(profile, prop, '-')
            )}
          </Typography>
        </Box>
      ))}
      {customKeys.map(customKey => (
        <Box key={customKey.technicalName} component="li" className={classes.listItem}>
          <Typography variant="body1" component="div" className={classes.property}>
            {customKey.name}
          </Typography>
          <Typography variant="body1" component="div" className={classes.value}>
            {renderCustomKeyValue(
              customKey.technicalName,
              customKey.type,
              timeDateFormat,
              profile.customKeys,
            )}
          </Typography>
        </Box>
      ))}
      <Box key="profileId" component="li" className={classes.listItem}>
        <Typography variant="body1" component="div" className={classes.property}>
          {t('event.report.profile.properties.profile.id')}
        </Typography>
        <Typography variant="body1" component="div" className={classes.value}>
          {profile?.id}
        </Typography>
      </Box>
      <Box key="referrerId" component="li" className={classes.listItem}>
        <Typography variant="body1" component="div" className={classes.property}>
          {t('event.report.profile.properties.referrer.id')}
        </Typography>
        <Typography variant="body1" component="div" className={classes.value}>
          {profile?.memberships?.[0]?.referrerId || '-'}
        </Typography>
      </Box>
      <Box key="membershipCreatedAt" component="li" className={classes.listItem}>
        <Typography variant="body1" component="div" className={classes.property}>
          {t('event.report.profile.properties.membership.createdAt')}
        </Typography>
        <Typography variant="body1" component="div" className={classes.value}>
          {membership?.createdAt ? formatDate(new Date(membership.createdAt), timeDateFormat) : '-'}
        </Typography>
      </Box>
      <Box key="membershipLastVisit" component="li" className={classes.listItem}>
        <Typography variant="body1" component="div" className={classes.property}>
          {t('event.report.profile.properties.membership.last-visit')}
        </Typography>
        <Typography variant="body1" component="div" className={classes.value}>
          {membership?.lastVisit ? formatDate(new Date(membership.lastVisit), timeDateFormat) : '-'}
        </Typography>
      </Box>
    </ul>
  );
}
